import { LayerType } from './enums/LayerType';
import { Tilemaps } from 'phaser'
import { createMap } from './components/TileMap'
import { NpcsAndObjects, createCharacterSprite } from './components/NpcAndObjects'
import GlobalInfo from '../GlobalInfo'
import { GridEngine, Position, Direction, CollisionStrategy } from 'grid-engine'
import { basicMovement } from './components/Characters'
import { adjustMood, PLAYER_ID, MOOD_META } from '../sim/moods'
import type { MoodKey } from '../sim/moods'
import type { Interaction } from './greybox/campusMaps'
import { loadKenneyAudio, KENNEY } from '../assets/assetLibrary'
import ServerLink from '../net/ServerLink'
import { floatFeedback, SFX_ACTION } from './feedback'

// Sprite du joueur : un·e élève Kenney (RPG Urban Pack — personnages modernes).
// VRAIE marche 4 directions : chaque direction = une COLONNE de la feuille (debout + 2 pas) ;
// le perso regarde dans la direction du déplacement et les jambes alternent (corps fixe).
//   col 23 = droite · 24 = bas (face) · 25 = haut (dos) · 26 = gauche ; lignes = debout, pas1, pas2.
// On charge la feuille AVEC espacement (tilemap.png, 1px) pour éviter le "bleeding" inter-frames.
export const PLAYER_TEXTURE = 'student'
const PLAYER_SHEET = '/assets/kenney/tier1-visual/rpg-urban-pack/Tilemap/tilemap.png'
const PLAYER_INITIAL_FRAME = 24 // de face (bas)
// Marche 4 directions réutilisée par les agents distants (RemoteAgentDriver).
export const PLAYER_WALK = {
    right: { standing: 26, leftFoot: 53, rightFoot: 80 },
    down: { standing: 24, leftFoot: 51, rightFoot: 78 },
    up: { standing: 25, leftFoot: 52, rightFoot: 79 },
    left: { standing: 23, leftFoot: 50, rightFoot: 77 },
}

export default abstract class GameScene extends Phaser.Scene {

    // Grid Engine info
    gridEngine!: GridEngine;
    gridEngineSettings: {
        startPosition: {
            x: number;
            y: number;
        };
        scale: number;
        characterCollisionStrategy: CollisionStrategy;
        layerOverlay: boolean;
    };

    // direction keys
    cursors!: Phaser.Types.Input.Keyboard.CursorKeys;

    // interaction / menu keys
    interactionKey!: Phaser.Input.Keyboard.Key;
    backKey!: Phaser.Input.Keyboard.Key;

    // main player — the human always embodies Caz, l'élève fantôme (a translucent kid spirit).
    playerSprite!: Phaser.Physics.Arcade.Sprite;
    playerName!: string;
    avatarScale: number = 1.3;
    // Caz spectral presence: a "Caz" nameplate that tracks the player sprite.
    private cazLabel?: Phaser.GameObjects.Text;

    sceneName!: string;
    map!: Tilemaps.Tilemap;
    npcsAndObjectsArray: NpcsAndObjects[] = [];
    characterMoved: boolean = false;

    // to load images
    imageNames!: {
        Map: string, //map is mandatory for the scene
        [index: string]: string
    };
    imageMapDefaultPath: string;
    tilemapJSONPath!: string;
    layerNames: LayerType[];
    imageMapNames!: {
        [index: string]: {
            name: string;
            path?: string;
        };
    };

    constructor(
        name: string,
        xPos?: number,
        yPos?: number,
        layerNames?: LayerType[]) {
        super(name);
        this.sceneName = name
        this.playerName = `${name}_perli`
        this.imageMapDefaultPath = 'tilesets/'
        this.layerNames = layerNames || Object.values(LayerType)
        this.gridEngineSettings = {
            startPosition: {
                x: xPos ?? 1,
                y: yPos ?? 1
            },
            scale: 1,
            characterCollisionStrategy: CollisionStrategy.BLOCK_ONE_TILE_AHEAD,
            layerOverlay: false
        }
    }

    // load here images common in all scenes
    preload(): void {
        this.load.image('emptyDoorGraphic', '../assets/images/emptyDoorGraphic.png')
        loadKenneyAudio(this, SFX_ACTION, KENNEY.audio.uiClick) // son des props interactifs
    }

    create(): void {
        // asign logic to keys
        this.cursors = this.input.keyboard.createCursorKeys()
        this.interactionKey = this.input.keyboard.addKey('E')
        this.backKey = this.input.keyboard.addKey('ESC')

        // create map, npcs, objects and start grid engine
        this.createMap();
        this.initiateGridEngine();
        this.createCamera(this.map.widthInPixels, this.map.heightInPixels);
        this.createNpcs();
        basicMovement(this);
        NpcsAndObjects.interaction(this)

        // NB: les overlays (dialogue, HUD, météo, Journal, minimap, contrôles tactiles) sont lancés UNE fois
        // au boot dans game.ts (sur l'événement READY), AVANT le switch "server-spawn" — découplés du create()
        // d'une scène de lieu. Ne PAS les relancer ici (sinon double-lancement selon le timing du boot).

        // Publie la scène de jeu active (lue par ControlsScene pour piloter le bon joueur).
        // WAKE couvre les changements de lieu via les portes (scene.switch endort/réveille).
        const markActive = (): void => { GlobalInfo._gameProgress.activeScene = this.sceneName }
        markActive()
        this.events.on(Phaser.Scenes.Events.WAKE, markActive)

        // Relais clavier -> overlay : les events clavier d'une scène de JEU arrivent bien
        // (contrairement à ceux d'une scène overlay lancée). E = avancer/fermer, Échap = fermer.
        this.input.keyboard.on('keydown-E', () => {
            if (GlobalInfo._gameProgress.inDialogue) { GlobalInfo.emit('dialogue-advance'); return; }
            // E face à un Kazi -> ouvre sa fiche + le fait parler (interact).
            const kazi = this.kaziInFront();
            if (kazi) this.openKazi(kazi);
        });
        this.input.keyboard.on('keydown-ESC', () => {
            if (GlobalInfo._gameProgress.inDialogue) { GlobalInfo.emit('dialogue-close'); return; }
            GlobalInfo.emit('close-journal');
        });
        // J = ouvre/ferme le Journal (les scènes overlay ne reçoivent pas le clavier → relais).
        this.input.keyboard.on('keydown-J', () => {
            if (GlobalInfo._gameProgress.inDialogue) return;
            GlobalInfo.emit('toggle-journal');
        });
        // M = affiche/masque le plan du collège (minimap).
        this.input.keyboard.on('keydown-M', () => {
            if (GlobalInfo._gameProgress.inDialogue) return;
            GlobalInfo.emit('toggle-minimap');
        });
        // C = ouvre/ferme « Les Chambres » (l'annuaire des chambres — le « lien » vers Chez moi).
        this.input.keyboard.on('keydown-C', () => {
            if (GlobalInfo._gameProgress.inDialogue) return;
            GlobalInfo.emit('toggle-chambres');
        });

        // TAP/clic sur un Kazi -> ouvre sa fiche (humeurs). Ignoré si un panneau est déjà ouvert.
        // Le registre tuile->personnage est rempli par CharacterFactory.spawnCharactersForLocation.
        this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
            if ((GlobalInfo as any)._gameProgress.journalOpen) return;
            if (GlobalInfo._gameProgress.inDialogue) return;
            // Ignore les taps qui tombent sur un contrôle tactile (joystick / bouton d'action) :
            // rectangles en coords canvas partagées entre scènes (cf. ControlsScene.publishZones).
            const zones = GlobalInfo._gameProgress.controlZones as Array<{ x: number; y: number; w: number; h: number }> | undefined;
            if (zones) {
                for (const z of zones) {
                    if (pointer.x >= z.x && pointer.x <= z.x + z.w && pointer.y >= z.y && pointer.y <= z.y + z.h) return;
                }
            }
            const wp = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
            const tx = Math.floor(wp.x / 16), ty = Math.floor(wp.y / 16);
            const reg = (this as any).kaziByTile as Record<string, any> | undefined;
            const kazi = reg && reg[`${tx},${ty}`];
            if (kazi) {
                this.openKazi(kazi);
                return;
            }
            // TAP sur un PROP interactif -> action (modifie les humeurs du joueur + feedback).
            const ireg = (this as any).interactablesByTile as Record<string, Interaction> | undefined;
            const it = ireg && ireg[`${tx},${ty}`];
            if (it) this.doInteraction(it, tx, ty);
        });
    }

    // Ouvre la fiche d'un Kazi ET le fait parler au joueur (interact -> ligne Opus côté serveur).
    private openKazi(kazi: { id: string; displayName: string; role?: string; dialogueHook?: string }): void {
        GlobalInfo.emit('show-kazi', {
            id: kazi.id, displayName: kazi.displayName,
            role: kazi.role ?? '', dialogueHook: kazi.dialogueHook ?? '',
        });
        if (ServerLink.enabled) ServerLink.interact(kazi.id);
    }

    // Kazi sur la case devant le joueur (ou sur sa case), via le registre kaziByTile.
    private kaziInFront(): { id: string; displayName: string; role?: string; dialogueHook?: string } | undefined {
        const reg = (this as any).kaziByTile as Record<string, any> | undefined;
        if (!reg) return undefined;
        const pos = this.gridEngine.getPosition(this.playerName);
        const dir = this.gridEngine.getFacingDirection(this.playerName);
        let dx = 0, dy = 0;
        if (dir === Direction.UP) dy = -1;
        else if (dir === Direction.DOWN) dy = 1;
        else if (dir === Direction.LEFT) dx = -1;
        else if (dir === Direction.RIGHT) dx = 1;
        return reg[`${pos.x + dx},${pos.y + dy}`] || reg[`${pos.x},${pos.y}`];
    }

    // Effet d'un prop : applique les deltas d'humeur au joueur + texte flottant de feedback.
    private propCooldown: Record<string, number> = {}
    private doInteraction(it: Interaction, tx: number, ty: number): void {
        const key = `${tx},${ty}`
        const now = this.time.now
        if (this.propCooldown[key] && now - this.propCooldown[key] < 700) return
        this.propCooldown[key] = now
        const parts: string[] = []
        for (const k of Object.keys(it.effects)) {
            const delta = (it.effects as Record<string, number>)[k]
            adjustMood(PLAYER_ID, k as MoodKey, delta)
            const meta = MOOD_META.find(mm => mm.key === k)
            parts.push((delta >= 0 ? '+' : '') + delta + ' ' + (meta ? meta.label : k))
        }
        if (this.cache.audio.exists(SFX_ACTION)) this.sound.play(SFX_ACTION, { volume: 0.5 })
        // Surface the creator-authored blurb + co-authors so authoring is visible at point of use.
        const extra = [it.blurb ? `« ${it.blurb} »` : '', it.coCreators?.length ? `déco : ${it.coCreators.join(', ')}` : '']
            .filter(Boolean).join('\n')
        this.floatText(tx * 16 + 8, ty * 16, `${it.label}\n${parts.join('  ')}${extra ? '\n' + extra : ''}`)
    }

    // Texte flottant (monte et s'estompe) — délègue au helper partagé (cf. feedback.ts).
    private floatText(wx: number, wy: number, text: string): void {
        floatFeedback(this, wx, wy, text)
    }

    update(): void {
        this.syncCazPresence() // keep Caz's glow + nameplate tracking the sprite, even mid-dialogue
        // Pendant un dialogue : on gèle les actions du joueur (l'overlay gère ses propres entrées).
        if (GlobalInfo._gameProgress.inDialogue) return;
    }

    // used in scenes to load objects images
    abstract loadObjectImages(): void

    // used by scenes to create NPCS
    abstract createNpcs(): void

    /** Not a standard method of Phaser.Scene.
     * Resets the scene completely.
     * Needed because of own implementations like the npcsAndObjectsArray.
     */
    reset(
        /** If set to true, the position and orientation of the player character will be kept. */
        keepCharacterPosition: boolean = true,
    ): void {
        const oldPosition: Position = this.gridEngine.getPosition(this.playerName)
        const oldDirection: Direction = this.gridEngine.getFacingDirection(this.playerName)

        this.npcsAndObjectsArray = []
        this.gridEngine.removeAllCharacters()
        this.scene.restart()
        /* updates scene manager to restart immediately
           (always restarts with next scene manager update)
          */
        this.scene.manager.update(0, 0)
        if (keepCharacterPosition) {
            this.gridEngine.setPosition(this.playerName, oldPosition)
            this.gridEngine.turnTowards(this.playerName, oldDirection)
        }
    }

    // load the main character (feuille RPG Urban Pack, 16×16, espacement 1px ; clé partagée)
    loadAvatarSpritesheet(): void {
        if (this.textures.exists(PLAYER_TEXTURE)) return
        this.load.spritesheet(PLAYER_TEXTURE, PLAYER_SHEET,
            { frameWidth: 16, frameHeight: 16, spacing: 1 }
        );
    }

    // load map resources
    loadMapImages(): void {
        // Tilemap-Bilder laden
        Object.keys(this.imageMapNames).forEach(key => {
            // Use image-specific path, if defined; otherwise use default path
            const path = (this.imageMapNames[key].path != null) ? this.imageMapNames[key].path : this.imageMapDefaultPath

            this.load.image(
                this.imageMapNames[key].name,
                `${path ?? ''}${this.imageMapNames[key].name}.png`)
        })

        this.load.tilemapTiledJSON(this.imageNames.Map, this.tilemapJSONPath)
    }

    // prepare tilesets and create the map
    createMap(): void {
        const tilesetInfo = Object.keys(this.imageMapNames).map(key => {
            return {
                tilesetName: this.imageMapNames[key].name,
                image: (this.imageMapNames[key].name)
            }
        })

        this.map = createMap({
            scene: this, tilemapTiledJSONKey: this.imageNames.Map, tilesetInfo: tilesetInfo, layerNames: this.layerNames
        }).tilemap
    }

    // GridEngine methods should only be used after this method was called!**
    initiateGridEngine(): void {
        this.playerSprite = createCharacterSprite(this, 0, 0, PLAYER_TEXTURE, this.avatarScale)
        this.playerSprite.setFrame(PLAYER_INITIAL_FRAME)
        this.createCazPresence()
        const gridEngineConfig = {
            characters: [
                {
                    id: this.playerName,
                    sprite: this.playerSprite,
                    startPosition: this.gridEngineSettings.startPosition,
                    // marche 4 directions : le perso regarde où il va, jambes qui alternent.
                    walkingAnimationMapping: PLAYER_WALK,
                },
            ],
            layerOverlay: this.gridEngineSettings.layerOverlay,
            characterCollisionStrategy: this.gridEngineSettings.characterCollisionStrategy
        }
        this.gridEngine.create(this.map, gridEngineConfig)
    }

    // Caz, l'élève fantôme: reskin the existing player sprite as a translucent kid spirit —
    // a floating "Caz" nameplate, no new art. Synced to the sprite each frame in update().
    private createCazPresence(): void {
        this.playerSprite.setAlpha(0.72) // spectral translucency
        this.cazLabel = this.add.text(0, -999, 'Caz', {
            fontFamily: 'sans-serif', fontSize: '9px', color: '#d8f0ff',
            backgroundColor: '#10151fcc', padding: { x: 3, y: 1 }, align: 'center',
        }).setOrigin(0.5, 1).setDepth(960).setResolution(3)
        this.syncCazPresence()
    }

    // Keep the nameplate above the head (counter-scaled to the gameplay zoom so the label stays
    // readable). Cheap; called every frame.
    private syncCazPresence(): void {
        if (!this.playerSprite) return
        const b = this.playerSprite.getBounds()
        if (this.cazLabel) {
            const z = this.cameras.main.zoom || 1
            this.cazLabel.setPosition(b.centerX, b.top - 2).setScale(1 / z)
        }
    }

    // create cameras
    createCamera(boundLimitX: number, boundLimitY: number): void {
        // caméra qui suit le perso ; le zoom s'ADAPTE à la carte pour remplir l'écran (mode RESIZE).
        this.cameras.main.setBounds(0, 0, boundLimitX, boundLimitY);
        this.cameras.main.startFollow(this.playerSprite, true);
        this.fitCameraZoom(boundLimitX, boundLimitY)
        // Recalcule le zoom quand l'écran change (rotation / resize plein écran).
        this.scale.on('resize', () => this.fitCameraZoom(boundLimitX, boundLimitY))
    }

    // Zoom adapté à la carte (mode RESIZE plein écran) :
    //  - GRANDE carte (campus) : « cover » => remplit l'écran et suit le joueur.
    //  - PETITE salle (intérieur) : « fit » => toute la pièce visible (PNJ des bords compris),
    //    avec une marge sombre #1a202c (le bas accueille les contrôles tactiles).
    // Le centrage des cartes plus petites que la vue est géré par Phaser (bounds < viewport).
    private fitCameraZoom(mapW: number, mapH: number): void {
        const vw = this.scale.width, vh = this.scale.height
        if (!vw || !vh) return
        const fit = Math.min(vw / mapW, vh / mapH)
        const cover = Math.max(vw / mapW, vh / mapH)
        // cover > 3 = la carte est petite (le « cover » zoomerait trop et rognerait) => on montre tout.
        const zoom = cover > 3 ? Phaser.Math.Clamp(fit, 1.5, 4) : Phaser.Math.Clamp(cover, 2, 3)
        this.cameras.main.setZoom(zoom)
    }

    switch(key: string | Phaser.Scene): void {
        this.scene.scene.sound.stopAll()
        this.scene.switch(key)
    }
}